import { StandardEvents } from '@shopify/events';

export function countCartItems(cart) {
  if (!Array.isArray(cart?.items)) throw new Error('Invalid cart response');
  return cart.items.reduce((total, item) => {
    let units = item.handle === 'snoutlabs-ultrasonic-dog-training-device'
      ? Number.parseInt(item.variant_options?.[1], 10)
      : 1;
    if (![1, 2, 3].includes(units)) units = 1;
    const quantity = Number(item.quantity);
    if (!Number.isSafeInteger(quantity) || quantity < 0) throw new Error('Invalid cart quantity');
    return total + units * quantity;
  }, 0);
}

class SnCartStatus extends HTMLElement {
  controller = null;

  connectedCallback() {
    document.addEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate);
    document.addEventListener('sn:cart-change', this.onCartUpdate);
    document.addEventListener('visibilitychange', this.onVisibilityChange);
    window.addEventListener('pageshow', this.onPageShow);
    if (performance.getEntriesByType('navigation')[0]?.type === 'back_forward') this.refresh();
  }

  disconnectedCallback() {
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate);
    document.removeEventListener('sn:cart-change', this.onCartUpdate);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    window.removeEventListener('pageshow', this.onPageShow);
    this.controller?.abort();
    this.controller = null;
  }

  onCartUpdate = (event) => {
    if (event.promise) {
      event.promise.then(() => this.refresh()).catch(() => {});
    } else {
      this.refresh();
    }
  };

  onVisibilityChange = () => {
    if (document.visibilityState === 'visible') this.refresh();
  };

  onPageShow = (event) => {
    if (event.persisted) this.refresh();
  };

  async refresh() {
    if (!this.isConnected) return;
    this.controller?.abort();
    const controller = new AbortController();
    this.controller = controller;
    try {
      const response = await fetch(this.dataset.cartUrl, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
        cache: 'no-store',
        signal: controller.signal,
      });
      if (!response.ok) return;
      const cart = await response.json();
      if (controller.signal.aborted || this.controller !== controller || !this.isConnected) return;
      const count = countCartItems(cart);
      const badge = this.querySelector('[data-sn-cart-badge]');
      const link = this.querySelector('.sn-header-cart');
      if (!badge || !link) return;
      badge.textContent = count > 99 ? '99+' : String(count);
      badge.hidden = count === 0;
      const itemLabel = count === 1 ? this.dataset.oneLabel : this.dataset.otherLabel.replace('__count__', String(count));
      link.setAttribute('aria-label', this.dataset.cartLabel + ', ' + itemLabel);
    } catch {
    } finally {
      if (this.controller === controller) this.controller = null;
    }
  }
}

if (!customElements.get('sn-cart-status')) {
  customElements.define('sn-cart-status', SnCartStatus);
}
